/**
 * PROCEDURAL PROP TEXTURES.
 *
 * The prop kit ships with hand-authored PBR materials and no maps, which reads
 * well on small fast-moving units but leaves large static structures — barns,
 * silos, hangars — looking like flat coloured boxes.
 *
 * These are generated once at load, tile seamlessly, and are shared by every
 * instance of a material, so the cost is a handful of canvases rather than a
 * per-object texture budget.
 *
 * Design rule: the texture supplies BREAK-UP, not detail. At gameplay scale
 * the player reads value variation and directionality — planks running
 * vertically, corrugation running around a silo — long before they read grain.
 */

import * as THREE from 'three'

/**
 * Assign a procedural surface by material NAME.
 *
 * Shared by every system that displays a forged model, so the prop field, the
 * boss and the midboss all resolve the same material to the same surface. It
 * previously lived inside PropField, which meant the boss — the single largest
 * object in the stage — rendered entirely in flat colour.
 *
 * Returns null for materials that must stay unmapped: emissives are light
 * sources and a map destroys the read, water is driven by clearcoat rather
 * than by a texture, and small fast units resolve as silhouettes where a
 * texture is pure noise.
 */
export function textureForMaterial(
  materialName: string,
  color: THREE.Color,
): THREE.Texture | null {
  const hex = `#${color.getHexString()}`
  const key = `${materialName}:${hex}`
  const cached = SURFACE_CACHE.get(key)
  if (cached !== undefined) return cached

  let tex: THREE.Texture | null = null
  switch (materialName) {
    // Board siding — the plank rhythm is the read.
    case 'WeatheredWood':
    case 'Whitewash':
    case 'ChurchWhite':
    case 'DarkTimber':
      tex = createPlankTexture(hex, 7)
      break
    // Folded sheet — silos, bins, hangars, machine sheds.
    case 'Galvanised':
    case 'Rust':
    case 'HullPanel':
    case 'StructuralSteel':
      tex = createCorrugatedTexture(hex, 11)
      break
    // Standing-seam roofing. Reads correctly from directly above, where a
    // shingle grid collapses into speckle.
    case 'BarnRed':
    case 'TinRoof':
    case 'Shingle':
      tex = createRoofTexture(hex, 19)
      break
    // Aggregate and form-panel joints.
    case 'Concrete':
    case 'Gravel':
    case 'Asphalt':
    case 'Fieldstone':
    case 'Brick':
    case 'SandTan':
      tex = createConcreteTexture(hex, 23)
      break
    // Military hardware large enough to read: boss chassis, revetments,
    // hangar skin. Coarser pitch than farm sheet metal.
    case 'OliveDrab':
    case 'HullGrey':
    case 'NavyGrey':
    case 'NavyGreyDark':
      tex = createCorrugatedTexture(hex, 29)
      break
    default:
      break
  }
  SURFACE_CACHE.set(key, tex)
  return tex
}

const SURFACE_CACHE = new Map<string, THREE.Texture | null>()
/** Materials already upgraded, so a shared material is only cloned once. */
const UPGRADED = new WeakMap<THREE.Material, THREE.Material>()

/**
 * Walk a loaded model and give every mapped material its procedural surface.
 *
 * Use for models rendered as-is (boss, midboss). PropField does the same
 * assignment itself because it merges geometry per material before
 * instancing, and needs the texture at that point.
 */
export function applySurfaceTextures(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh
    if (!m.isMesh) return
    const apply = (mat: THREE.Material): THREE.Material => {
      const existing = UPGRADED.get(mat)
      if (existing) return existing
      const std = mat as THREE.MeshStandardMaterial
      if (!std.color) return mat
      const tex = textureForMaterial(mat.name, std.color)
      if (!tex) {
        UPGRADED.set(mat, mat)
        return mat
      }
      const next = std.clone()
      next.map = tex
      // Let the map carry the value so the base colour does not double-darken it.
      next.color.setScalar(1)
      next.needsUpdate = true
      UPGRADED.set(mat, next)
      return next
    }
    m.material = Array.isArray(m.material) ? m.material.map(apply) : apply(m.material)
  })
}

function rng(seed: number) {
  let v = seed >>> 0
  return () => {
    v += 0x6d2b79f5
    let n = v
    n = Math.imul(n ^ (n >>> 15), n | 1)
    n ^= n + Math.imul(n ^ (n >>> 7), n | 61)
    return ((n ^ (n >>> 14)) >>> 0) / 4294967296
  }
}

function makeCanvas(size = 256) {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  return c
}

function finish(canvas: HTMLCanvasElement, repeatX = 1, repeatY = 1): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(repeatX, repeatY)
  tex.anisotropy = 4
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/**
 * Vertical board siding. The dominant read on a barn is the plank rhythm and
 * the occasional darker weathered board — not wood grain.
 */
export function createPlankTexture(base: string, seed = 7): THREE.CanvasTexture {
  const size = 256
  const c = makeCanvas(size)
  const g = c.getContext('2d')!
  const rand = rng(seed)

  g.fillStyle = base
  g.fillRect(0, 0, size, size)

  const boards = 16
  const w = size / boards
  for (let i = 0; i < boards; i += 1) {
    // Per-board value shift — this is what stops it reading as a flat panel.
    const shade = 0.82 + rand() * 0.34
    g.globalAlpha = 0.5
    g.fillStyle = shade > 1 ? '#ffffff' : '#000000'
    g.globalAlpha = Math.abs(shade - 1) * 0.7
    g.fillRect(i * w, 0, w, size)

    // Seam between boards.
    g.globalAlpha = 0.45
    g.fillStyle = '#000000'
    g.fillRect(i * w, 0, 1.5, size)
  }

  // Horizontal batten rails — barns have them and they break the verticals.
  g.globalAlpha = 0.28
  g.fillStyle = '#000000'
  for (const y of [size * 0.28, size * 0.68]) g.fillRect(0, y, size, 3)

  g.globalAlpha = 1
  return finish(c, 1, 1)
}

/**
 * Corrugated metal. Silos, grain bins, hangars, machine sheds.
 * The rhythm is the whole read; keep it high-contrast and regular.
 */
export function createCorrugatedTexture(base: string, seed = 11): THREE.CanvasTexture {
  const size = 256
  const c = makeCanvas(size)
  const g = c.getContext('2d')!
  const rand = rng(seed)

  g.fillStyle = base
  g.fillRect(0, 0, size, size)

  const ribs = 32
  const w = size / ribs
  for (let i = 0; i < ribs; i += 1) {
    // Each rib is a lit face and a shadowed face — that pair is what makes
    // flat geometry read as folded sheet.
    g.globalAlpha = 0.22
    g.fillStyle = '#ffffff'
    g.fillRect(i * w, 0, w * 0.45, size)
    g.globalAlpha = 0.3
    g.fillStyle = '#000000'
    g.fillRect(i * w + w * 0.55, 0, w * 0.45, size)
  }

  // Horizontal panel joints with a little rust bleed under each.
  g.globalAlpha = 0.35
  for (let y = 0; y < size; y += size / 4) {
    g.fillStyle = '#000000'
    g.fillRect(0, y, size, 2)
    g.globalAlpha = 0.12
    g.fillStyle = '#6e4229'
    g.fillRect(0, y + 2, size, 6 + rand() * 5)
    g.globalAlpha = 0.35
  }

  g.globalAlpha = 1
  return finish(c, 3, 2)
}

/**
 * Asphalt shingle / weathered roof. Used on farmhouse and barn roofs, where a
 * flat red plane is the single most obviously untextured surface in the stage.
 */
export function createRoofTexture(base: string, seed = 19): THREE.CanvasTexture {
  const size = 256
  const c = makeCanvas(size)
  const g = c.getContext('2d')!
  const rand = rng(seed)

  g.fillStyle = base
  g.fillRect(0, 0, size, size)

  // Standing-seam metal roofing rather than shingles: long vertical seams read
  // correctly from directly above at any distance, where a shingle grid
  // collapses into speckle.
  const seams = 10
  const w = size / seams
  for (let i = 0; i < seams; i += 1) {
    // Gentle per-panel value shift.
    g.globalAlpha = 0.06 + rand() * 0.06
    g.fillStyle = rand() > 0.5 ? '#ffffff' : '#000000'
    g.fillRect(i * w, 0, w, size)
    // The raised seam itself: a bright edge against a soft shadow.
    g.globalAlpha = 0.22
    g.fillStyle = '#ffffff'
    g.fillRect(i * w, 0, 2, size)
    g.globalAlpha = 0.2
    g.fillStyle = '#000000'
    g.fillRect(i * w + 2, 0, 3, size)
  }
  // One horizontal ridge break, low contrast.
  g.globalAlpha = 0.12
  g.fillStyle = '#000000'
  g.fillRect(0, size * 0.5, size, 2)

  g.globalAlpha = 1
  return finish(c, 1, 1)
}

/**
 * CROP ROWS. Planted ground seen from directly above is corduroy — a dense
 * regular ridge rhythm running whichever way the tractor drove.
 *
 * Deliberately greyscale so one texture serves every crop: the parcel material
 * supplies the colour and this supplies only the rhythm. Stripes run along U,
 * so a parcel chooses its row direction purely by how it assigns UVs.
 */
export function createCropRowTexture(seed = 31): THREE.CanvasTexture {
  const size = 128
  const c = makeCanvas(size)
  const g = c.getContext('2d')!
  const rand = rng(seed)

  g.fillStyle = '#ffffff'
  g.fillRect(0, 0, size, size)

  // Ridge and furrow. The lit crest and shadowed trough as a PAIR is what
  // makes a flat plane read as ploughed — a single line reads as a scratch.
  const rows = 16
  const w = size / rows
  for (let i = 0; i < rows; i += 1) {
    g.globalAlpha = 0.16
    g.fillStyle = '#ffffff'
    g.fillRect(i * w, 0, w * 0.42, size)
    g.globalAlpha = 0.3
    g.fillStyle = '#000000'
    g.fillRect(i * w + w * 0.55, 0, w * 0.4, size)
  }

  // Break the perfect regularity — patchy growth along the rows. Without this
  // the corduroy reads as a manufactured pattern rather than as a crop.
  for (let i = 0; i < 900; i += 1) {
    g.globalAlpha = 0.03 + rand() * 0.07
    g.fillStyle = rand() > 0.45 ? '#000000' : '#ffffff'
    const h = 3 + rand() * 12
    g.fillRect(rand() * size, rand() * size, 1 + rand() * 2, h)
  }

  g.globalAlpha = 1
  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.anisotropy = 8
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/** Poured concrete — revetments, aprons, bunkers. Subtle by design. */
export function createConcreteTexture(base: string, seed = 23): THREE.CanvasTexture {
  const size = 256
  const c = makeCanvas(size)
  const g = c.getContext('2d')!
  const rand = rng(seed)

  g.fillStyle = base
  g.fillRect(0, 0, size, size)

  // Aggregate speckle.
  for (let i = 0; i < 2600; i += 1) {
    g.globalAlpha = 0.05 + rand() * 0.1
    g.fillStyle = rand() > 0.5 ? '#ffffff' : '#000000'
    const s = 1 + rand() * 2
    g.fillRect(rand() * size, rand() * size, s, s)
  }

  // Form-panel joints.
  g.globalAlpha = 0.22
  g.fillStyle = '#000000'
  for (const p of [size * 0.5]) {
    g.fillRect(p, 0, 2, size)
    g.fillRect(0, p, size, 2)
  }

  g.globalAlpha = 1
  return finish(c, 2, 2)
}
